/** 任务关联模型用量和价格快照；未知与已知小计分开显示，复用不冒充新增扣费。 */
import type { LandingAiModelCallDto, LandingAiUsageSummary } from '@novel-admin/shared';
import { Table } from '~/ui/Table';

function tokens(value: string | number | null) {
  return value === null ? '未知' : BigInt(value).toLocaleString('en-US');
}
function cost(value: string | null) {
  if (value === null) return '未知';
  // token 成本常低于一美分，不能套用两位小数格式把非零费用展示为 $0.00。
  const [whole, fraction = ''] = value.split('.');
  const trimmed = fraction.replace(/0+$/, '');
  return `$${whole}${trimmed ? `.${trimmed}` : ''}`;
}
export function LandingAiUsage({
  usage,
  compact = false,
}: {
  usage: LandingAiUsageSummary | undefined;
  compact?: boolean;
}) {
  if (!usage) return <p className="muted">AI 用量：历史记录未采集</p>;
  if (usage.tracking_status === 'REUSED')
    return <p className="muted">复用成功结果 · 本次新增调用 0 · 估算费用 $0</p>;
  const incomplete =
    usage.tracking_status === 'LEGACY_UNKNOWN' ||
    usage.unknown_usage_calls > 0 ||
    usage.unpriced_calls > 0;
  return (
    <div className="vstack" aria-label="AI 用量与费用">
      <div className="mono">
        {incomplete ? '已知' : ''}输入 {tokens(usage.input_tokens)} · 输出{' '}
        {tokens(usage.output_tokens)} token · {usage.call_count} 次调用
      </div>
      <div className="mono">
        缓存输入 {tokens(usage.cached_input_tokens)} ·{' '}
        {usage.estimated_cost_usd === null
          ? `已知费用小计 ${cost(usage.known_cost_usd)}；完整费用未知`
          : `估算费用 ${cost(usage.estimated_cost_usd)} USD`}
      </div>
      {incomplete && (
        <p className="muted">
          {usage.tracking_status === 'LEGACY_UNKNOWN' ? '包含未跟踪的历史运行；' : ''}未知用量{' '}
          {usage.unknown_usage_calls} 次 · 未定价 {usage.unpriced_calls} 次
        </p>
      )}
      {!compact && (
        <p className="muted">
          按服务商返回的用量及调用时已核实单价估算，以账单为准。缓存未报告时按普通输入价估算。本运行包含自动重试；人工重试的历史调用留在原任务。活跃共享运行可能被多个任务引用，跨任务请勿重复累加。
        </p>
      )}
    </div>
  );
}
export function LandingAiCallDetails({
  calls,
  total,
}: { calls: LandingAiModelCallDto[]; total: number }) {
  return (
    <details>
      <summary>
        模型调用明细（{calls.length}/{total}）
      </summary>
      {calls.length < total && (
        <p className="muted">明细已截断，仅展示本次详情返回的前 200 条调用；汇总包含全部调用。</p>
      )}
      <Table>
        <thead>
          <tr>
            <th>模型 / 阶段</th>
            <th>用量 / 估算</th>
            <th>当次单价 USD / 百万 token</th>
          </tr>
        </thead>
        <tbody>
          {calls.map((call) => (
            <tr key={call.id}>
              <td>
                <div>{call.provider}</div>
                <div className="mono" style={{ overflowWrap: 'anywhere' }}>
                  {call.model}
                </div>
                <div>
                  {call.stage === 'review' ? 'AI 审核' : 'AI 脱敏'}
                  {call.batch_index === null ? '' : ` · 第 ${call.batch_index + 1} 批`}
                </div>
                <div>
                  {call.status === 'STARTED'
                    ? '调用已登记，结果未知'
                    : call.status === 'FAILED'
                      ? '调用失败'
                      : '模型已返回'}
                </div>
                <span className="mono">{call.created_at}</span>
              </td>
              <td>
                <div className="mono">输入 {tokens(call.usage?.input_tokens ?? null)}</div>
                <div className="mono">输出 {tokens(call.usage?.output_tokens ?? null)}</div>
                <div className="mono">缓存 {tokens(call.usage?.cached_input_tokens ?? null)}</div>
                <div className="mono">{cost(call.estimated_cost_usd)}</div>
              </td>
              <td>
                {call.price_snapshot ? (
                  <>
                    <div className="mono">输入 {call.price_snapshot.input_per_million}</div>
                    <div className="mono">输出 {call.price_snapshot.output_per_million}</div>
                    <div className="mono">
                      缓存 {call.price_snapshot.cached_input_per_million ?? '未定价'}
                    </div>
                    <a href={call.price_snapshot.source_url} target="_blank" rel="noreferrer">
                      价格来源
                    </a>
                    <div>核实于 {call.price_snapshot.verified_on}</div>
                  </>
                ) : (
                  '未定价'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </details>
  );
}
